import { EditButton } from "@/components/admin/edit-button";
import { ShowBase, useShowContext, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DateField } from "@/components/admin/date-field";

import type { Campaign } from "../types";
import { useIsCampaignAdmin } from "./useIsCampaignAdmin";
import { CampaignTaskDefinitionList } from "./CampaignTaskDefinitionList";
import { CampaignOverview } from "./CampaignOverview";
import { CampaignMyTasks } from "./CampaignMyTasks";
import { CampaignTargetBadges } from "./CampaignTargetBadges";

export const CampaignShow = () => (
  <ShowBase>
    <CampaignShowContent />
  </ShowBase>
);

const CampaignShowContent = () => {
  const { record, isPending } = useShowContext<Campaign>();
  const translate = useTranslate();
  const isAdmin = useIsCampaignAdmin();

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex lg:mr-72 gap-6">
      <div className="flex-1 space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold">{record.name}</h1>
                {record.product_name && (
                  <p className="text-muted-foreground">{record.product_name}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge>{record.status}</Badge>
                {isAdmin && <EditButton />}
              </div>
            </div>
            {record.description && (
              <p className="text-sm">{record.description}</p>
            )}
            <div className="flex gap-6 text-sm text-muted-foreground">
              {record.start_date && (
                <span>
                  Start: <DateField source="start_date" record={record} showDate />
                </span>
              )}
              {record.end_date && (
                <span>
                  End: <DateField source="end_date" record={record} showDate />
                </span>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {translate("resources.campaigns.fields.targets", {
                  _: "Targets",
                })}
              </p>
              <CampaignTargetBadges campaignId={record.id} />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue={isAdmin ? "overview" : "my_tasks"}>
          <TabsList>
            {isAdmin && (
              <TabsTrigger value="overview">
                {translate("resources.campaigns.tabs.overview", {
                  _: "Overview",
                })}
              </TabsTrigger>
            )}
            <TabsTrigger value="definitions">
              {translate("resources.campaigns.tabs.definitions", {
                _: "Task definitions",
              })}
            </TabsTrigger>
            <TabsTrigger value="my_tasks">
              {translate("resources.campaigns.tabs.my_tasks", {
                _: "My tasks",
              })}
            </TabsTrigger>
          </TabsList>

          {isAdmin && (
            <TabsContent value="overview" className="pt-4">
              <CampaignOverview campaignId={record.id} />
            </TabsContent>
          )}
          <TabsContent value="definitions" className="pt-4">
            <CampaignTaskDefinitionList campaignId={record.id} />
          </TabsContent>
          <TabsContent value="my_tasks" className="pt-4">
            <CampaignMyTasks campaignId={record.id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
